import './recordflows.css'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	IonButton,
	IonContent,
	IonIcon,
	IonPage,
	useIonAlert,
	useIonRouter,
	useIonViewDidLeave,
	useIonViewWillEnter,
	useIonViewWillLeave,
} from '@ionic/react'
import { doc, getDoc } from 'firebase/firestore'
import { useLocation, useParams } from 'react-router-dom'
import { FaRedo, FaUndo } from 'react-icons/fa'
import { IoCloudDoneSharp, IoCloudOfflineSharp, IoCloudUpload } from 'react-icons/io5'
import { BsCloudArrowUp, BsCloudDownload } from 'react-icons/bs'
import { settingsSharp, recordingOutline, helpCircleOutline } from 'ionicons/icons'

import Header from './Header'
import {
	useAmViewingOwnTeam,
	useMyGame,
	useMyFlowForGame,
	useMyGames,
	useMyNumPeriods,
	useMyTeam,
	useQueryMyTeamHasPro,
	useSection,
	useUserHasPro,
	useClearGameFlowMapFlag,
	useDeleteFlow,
	queryDbid,
	useSaveFlow,
} from './data'
import { FLOW_COLOR_PRESETS } from './definitions'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { db, getFirebaseAuth } from './firebase'
import { canManageFlowData, getFlowRecorderSessionId, hasRecordedFlowData } from './flowPermissions'
import {
	FlowLocalBackupKey,
	getFlowDataSignature,
	loadLocalFlowBackup,
	removeLocalFlowBackup,
	saveLocalFlowBackup,
} from './flowLocalBackup'
import { FLOW_DATA_SCHEMA_VERSION } from './flowSchema'
import { copyFlowJsonToClipboard, presentFlowImportAlert } from './flowImportExport'
import { pushOrGoBackToPreviousPage } from './navigation'
import { formatPeriodNumber, getPeriodDisplayTerms } from './periodLabels'
import { shareUrl } from './shareUrl'
import { deriveTeamDisplayNames } from './teamNames'
import { formatDate, getUrl, labelRound } from './util'
import { getGameSharePreviewUrl } from './previewShareUrl'
import { useWakeLock } from './useWakeLock'
import { isFlowGameComplete } from './flowProgress'
import LoadingSpinner from './LoadingSpinner'
import GameInsightIcon from './GameInsightIcon'
import Icon from './Icon'
import 'swiper/css'
import 'swiper/css/pagination'
import TutorialRecordFlowsModal from './TutorialRecordFlows'
import TidyFlowMap from './TidyFlowMap'

type EditableFlowData = Omit<FlowData, 'setup'> & {
	setup?: FlowData['setup']
}

type RecordingState = {
	flowData: EditableFlowData
	currentEvent: FlowDraftEvent | null
}

type FlowSyncStatus = 'idle' | 'saving' | 'local-only' | 'failed'

type CourtMarker =
	| {
		type: 'cp'
		side: FlowTeamSide
	}
	| {
		type: 'turnover'
		side: FlowTeamSide
		zone: FlowEventZone
	}
	| {
		type: 'goal'
		side: FlowTeamSide
		cpSide: FlowTeamSide
	}

const DEFAULT_HOME_COLOR = FLOW_COLOR_PRESETS[5]
const DEFAULT_AWAY_COLOR = FLOW_COLOR_PRESETS[0]
const SUPER_GOAL_HOLD_MS = 1200
const EMPTY_QUARTER_HOLD_MS = 1200

const enterRecordFlowsFullscreen = () => {
	document.body.classList.add('record-flows-fullscreen')
}

const leaveRecordFlowsFullscreen = () => {
	document.body.classList.remove('record-flows-fullscreen')
}

// Flow recording uses Firebase anonymous auth as lightweight write protection
// and as a same-recorder ownership key. App-wide `user` deliberately ignores
// anonymous auth, so read Firebase Auth directly here instead of using useUser().
const useFlowAuthUid = () => {
	const [ uid, setUid ] = useState<UserId | null>(() => getFirebaseAuth().currentUser?.uid || null)

	useEffect(() => {
		const auth = getFirebaseAuth()
		setUid(auth.currentUser?.uid || null)
		return auth.onAuthStateChanged(user => setUid(user?.uid || null))
	}, [ ])

	return uid
}

const RecordFlows = () => {

	const sectionGame = useMyGame()
	const games = useMyGames()
	const team = useMyTeam()
	const section = useSection()
	const flowAuthUid = useFlowAuthUid()
	const numPeriods = useMyNumPeriods()
	const amViewingOwnTeam = useAmViewingOwnTeam()
	const queryMyTeamHasPro = useQueryMyTeamHasPro()
	const saveFlow = useSaveFlow()
	const deleteFlow = useDeleteFlow()
	const clearGameFlowMapFlag = useClearGameFlowMapFlag()
	const router = useIonRouter()
	const { game: routeGameId } = useParams<{ game: string }>()
	const location = useLocation()
	const routeSectionParams = new URLSearchParams(location.search)
	const routeTeamId = routeSectionParams.get('team')
	const routeSeasonId = routeSectionParams.get('season')
	const sectionMatchesRoute = (!routeTeamId || routeTeamId === section.team)
		&& (!routeSeasonId || routeSeasonId === section.season)
	const gameId = routeGameId || sectionGame?.id
	const routeFlow = useMyFlowForGame(gameId)
	const flow = routeFlow.data?.id === gameId ? routeFlow.data : null
	const flowIsLoading = !routeFlow.loaded
	const game = sectionMatchesRoute && gameId
		? games?.[gameId] || (sectionGame?.id === gameId ? sectionGame : null)
		: null
	const hasGameContext = sectionMatchesRoute && !!gameId && !!section.team && !!section.season
	const gameIsLoading = !sectionMatchesRoute || (hasGameContext && games === null)
	const gameDoesNotExist = !!gameId
		&& sectionMatchesRoute
		&& (
			!section.team
			|| !section.season
			|| (games !== null && !game)
		)
	const openSetupFromRoute = new URLSearchParams(location.search).get('setup') === '1' || new URLSearchParams(window.location.search).get('setup') === '1'

    const backSearchParams = new URLSearchParams(location.search)
	backSearchParams.delete('setup')

	const backSearch = backSearchParams.toString()
	const backHref = `/games${backSearch ? `?${backSearch}` : ''}`
	const recordFlowUrl = gameId ? getUrl({
		relative: true,
		page: `games/${gameId}/record-flows`,
		section,
	}) : null
	const flowInviteUrl = gameId ? getUrl({
		relative: true,
		page: `games/${gameId}/record-flows/invite`,
		section,
	}) : null
	const flowBackupKey: FlowLocalBackupKey | null = useMemo(() => (
		gameId && section.team && section.season
			? {
				teamId: section.team,
				seasonId: section.season,
				gameId,
			}
			: null
	), [ gameId, section.season, section.team ])

	const currentTeamId = section.team && section.team === team?.id ? section.team : null
	const [ presentAlert ] = useIonAlert()
	const flowRecorderSessionId = getFlowRecorderSessionId()
	const userHasPro = useUserHasPro()

	const initializedGameRef = useRef<GameId | null>(null)
	const lastRemoteUpdatedRef = useRef<number | null>(null)
	const lastSavedFlowSignatureRef = useRef<string | null>(null)

	const [ setupDraft, setSetupDraft ] = useState<FlowData['setup']>(createDefaultSetup())
	const [ setupEntrySignature, setSetupEntrySignature ] = useState<string | null>(null)
	const [ recordingState, setRecordingState ] = useState<RecordingState>(() => createRecordingState(gameId, null, numPeriods))
	const [ saveRevision, setSaveRevision ] = useState(0)
	const [ isEditingSetup, setIsEditingSetup ] = useState(false)
	const [ keepSetupOpenForExit, setKeepSetupOpenForExit ] = useState(openSetupFromRoute)
	const [ isDeletingFlow, setIsDeletingFlow ] = useState(false)
	const [ isInitializingFlow, setIsInitializingFlow ] = useState(true)
	const [ syncStatus, setSyncStatus ] = useState<FlowSyncStatus>('idle')
	const [ syncMessage, setSyncMessage ] = useState<string | null>(null)
	const [ isRetryingSave, setIsRetryingSave ] = useState(false)
	const [ canStartNewFlow, setCanStartNewFlow ] = useState<boolean | null>(null)
	const [ startAccessTeamId, setStartAccessTeamId ] = useState<TeamId | null>(null)
	const [ isCheckingStartAccess, setIsCheckingStartAccess ] = useState(false)
	const [ redoStack, setRedoStack ] = useState<RecordingState[]>([])
	const [ showConcurrentRecordingWarning, setShowConcurrentRecordingWarning ] = useState(false)
	const [ isHelpOpen, setIsHelpOpen ] = useState(false)
	const [ isSuperGoalArmed, setIsSuperGoalArmed ] = useState(false)
	const goalPressActiveRef = useRef(false)
	const superGoalArmedRef = useRef(false)
	const superGoalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const emptyQuarterPressActiveRef = useRef(false)
	const emptyQuarterArmedRef = useRef(false)
	const emptyQuarterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const isRecordingComplete = isFlowGameComplete(recordingState.flowData, numPeriods)

	useWakeLock(!!recordingState.flowData.setup && !isEditingSetup && !isRecordingComplete)

	useIonViewWillEnter(() => {
		enterRecordFlowsFullscreen()
	})

	useIonViewWillLeave(() => {
		leaveRecordFlowsFullscreen()
	})

	useIonViewDidLeave(() => {
		setKeepSetupOpenForExit(false)
	})

	useEffect(() => {
		enterRecordFlowsFullscreen()
		return () => {
			leaveRecordFlowsFullscreen()
		}
	}, [ ])

	useEffect(() => {
		if (openSetupFromRoute) {
			// Ionic changes the URL before the outgoing page has finished
			// animating, so keep its route-selected Setup view until didLeave.
			setKeepSetupOpenForExit(true)
		}
	}, [ openSetupFromRoute ])

	const clearSuperGoalTimer = useCallback(() => {
		if (superGoalTimerRef.current) {
			clearTimeout(superGoalTimerRef.current)
			superGoalTimerRef.current = null
		}
	}, [])

	const resetGoalPress = useCallback(() => {
		clearSuperGoalTimer()
		goalPressActiveRef.current = false
		superGoalArmedRef.current = false
		setIsSuperGoalArmed(false)
	}, [ clearSuperGoalTimer ])

	useEffect(() => clearSuperGoalTimer, [ clearSuperGoalTimer ])

	const clearEmptyQuarterTimer = useCallback(() => {
		if (emptyQuarterTimerRef.current) {
			clearTimeout(emptyQuarterTimerRef.current)
			emptyQuarterTimerRef.current = null
		}
	}, [])

	const resetEmptyQuarterPress = useCallback(() => {
		clearEmptyQuarterTimer()
		emptyQuarterPressActiveRef.current = false
		emptyQuarterArmedRef.current = false
	}, [ clearEmptyQuarterTimer ])

	useEffect(() => resetEmptyQuarterPress, [ resetEmptyQuarterPress ])

	const loadLatestFlow = useCallback(async (opt: { throwOnError?: boolean } = { }) => {
		if (!gameId || !section.team || !section.season) {
			return null
		}

		try {
			const snapshot = await getDoc(doc(db, queryDbid('flow', section), gameId))
			if (!snapshot.exists()) {
				return null
			}

			return {
				...(snapshot.data() as Omit<FlowData, 'id'>),
				id: gameId,
			} as FlowData
		} catch (error) {
			console.error('Failed to load latest flow data', error)
			if (opt.throwOnError) {
				throw error
			}
			return null
		}
	}, [ gameId, section ])

	const hydrateFromFlow = useCallback((nextFlow: FlowData | null) => {
		setRecordingState(createRecordingState(gameId, nextFlow, numPeriods))
		setSetupDraft(nextFlow?.setup || createDefaultSetup())
		setSetupEntrySignature(null)
		setSaveRevision(0)
		setIsEditingSetup(false)
		setRedoStack([])
		lastRemoteUpdatedRef.current = nextFlow?.updated || null
		const nextSignature = getFlowSyncSignature(nextFlow)
		lastSavedFlowSignatureRef.current = nextSignature
		setSyncStatus('idle')
		setSyncMessage(null)
		initializedGameRef.current = gameId || null
		setIsInitializingFlow(false)
	}, [ gameId, numPeriods ])

	const retrySaveFlow = useCallback(async () => {
		if (!gameId || !flowBackupKey || !recordingState.flowData.setup || isRetryingSave) {
			return
		}

		setIsRetryingSave(true)
		const dataToSave = buildPersistedFlowData(recordingState)
		const dataSignature = getFlowSyncSignature(dataToSave)
		lastSavedFlowSignatureRef.current = dataSignature
		saveLocalFlowBackup(flowBackupKey, dataToSave, {
			lastAttemptedAt: Date.now(),
			lastError: null,
		})

		if (!navigator.onLine) {
			setSyncStatus('local-only')
			setSyncMessage('Saved on this device only. Waiting for network.')
			setIsRetryingSave(false)
			return
		}

		setSyncStatus('saving')
		setSyncMessage(null)

		try {
			await saveFlow(gameId, dataToSave)
			if (removeLocalFlowBackupIfSignatureMatches(flowBackupKey, dataSignature)) {
				setSyncStatus('idle')
				setSyncMessage(null)
			} else {
				showPendingLocalFlowBackup(flowBackupKey, setSyncStatus, setSyncMessage)
			}
		} catch (error) {
			console.error('Failed to save flow data', error)
			const message = getFlowSaveErrorMessage(error)
			setSyncStatus('failed')
			setSyncMessage(message)
			saveLocalFlowBackup(flowBackupKey, dataToSave, {
				lastAttemptedAt: Date.now(),
				lastError: message,
			})
		} finally {
			setIsRetryingSave(false)
		}
	}, [ flowBackupKey, gameId, isRetryingSave, recordingState, saveFlow ])

	const exportFlowJson = useCallback(async () => {
		if (!recordingState.flowData.setup) {
			return
		}

		const dataToExport = buildPersistedFlowData(recordingState)
		const json = JSON.stringify(dataToExport, null, 2)
		await copyFlowJsonToClipboard(json, presentAlert)
	}, [ presentAlert, recordingState ])

	const importFlowJson = useCallback(() => {
		if (!gameId) {
			return
		}

		presentFlowImportAlert({
			gameId,
			presentAlert,
			onImport: importedFlow => {
				setRecordingState(createRecordingState(gameId, importedFlow, numPeriods))
				setSetupDraft(importedFlow.setup)
				setSetupEntrySignature(null)
				setIsEditingSetup(false)
				setRedoStack([])
				setShowConcurrentRecordingWarning(false)
				setSyncStatus('idle')
				setSyncMessage(null)
				lastRemoteUpdatedRef.current = null
				lastSavedFlowSignatureRef.current = null
				initializedGameRef.current = gameId
				setSaveRevision(value => value + 1)
			},
		})
	}, [ gameId, numPeriods, presentAlert ])

	useEffect(() => {
		initializedGameRef.current = null
		lastRemoteUpdatedRef.current = null
		lastSavedFlowSignatureRef.current = null
		setIsInitializingFlow(true)
		setRecordingState(createRecordingState(gameId, null, numPeriods))
		setSetupDraft(createDefaultSetup())
		setSetupEntrySignature(null)
		setSaveRevision(0)
		setIsEditingSetup(false)
		setRedoStack([])
		setShowConcurrentRecordingWarning(false)
		setSyncStatus('idle')
		setSyncMessage(null)
	}, [ gameId, numPeriods ])

	useEffect(() => {
		let cancelled = false

		if (!game) {
			setCanStartNewFlow(null)
			setStartAccessTeamId(null)
			setIsCheckingStartAccess(true)
			return
		}

		if (hasRecordedFlowData(flow)) {
			setCanStartNewFlow(true)
			setStartAccessTeamId(currentTeamId)
			setIsCheckingStartAccess(false)
			return
		}

		if (!currentTeamId) {
			setCanStartNewFlow(null)
			setStartAccessTeamId(null)
			setIsCheckingStartAccess(true)
			return
		}

		setCanStartNewFlow(null)
		setStartAccessTeamId(null)
		setIsCheckingStartAccess(true)
		void queryMyTeamHasPro()
			.then(teamHasPro => {
				if (!cancelled) {
					setCanStartNewFlow(teamHasPro)
					setStartAccessTeamId(currentTeamId)
				}
			})
			.catch(error => {
				console.error('Failed to check team Pro status', error)
				if (!cancelled) {
					setCanStartNewFlow(false)
					setStartAccessTeamId(currentTeamId)
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsCheckingStartAccess(false)
				}
			})

		return () => {
			cancelled = true
		}
	}, [ currentTeamId, flow, game, queryMyTeamHasPro ])

	useEffect(() => {
		if (
			!flowInviteUrl
			|| !game
			|| hasRecordedFlowData(flow)
			|| isCheckingStartAccess
			|| !currentTeamId
			|| startAccessTeamId !== currentTeamId
			|| canStartNewFlow !== false
		) {
			return
		}

		router.push(flowInviteUrl, 'forward', 'replace')
	}, [ canStartNewFlow, currentTeamId, flow, flowInviteUrl, game, isCheckingStartAccess, router, startAccessTeamId ])

	useEffect(() => {
		if (!game || !gameId || flowIsLoading || initializedGameRef.current === gameId) {
			return
		}

		let cancelled = false

		const initialize = async () => {
			let nextFlow = flow

			if (!hasRecordedFlowData(nextFlow)) {
				nextFlow = await loadLatestFlow()
			}

			if (!cancelled) {
				const backup = flowBackupKey ? loadLocalFlowBackup(flowBackupKey) : null
				const hasRemoteFlow = hasRecordedFlowData(nextFlow)
				const hasBackupFlow = hasRecordedFlowData(backup?.flow)
				const remoteSignature = getFlowSyncSignature(nextFlow)
				const backupMatchesRemote = !!backup && remoteSignature === backup.signature
				const recoveredBackup = hasBackupFlow
					&& shouldRecoverLocalFlowBackup(backup!.flow, nextFlow, backup!.signature, remoteSignature)
				hydrateFromFlow(recoveredBackup ? backup!.flow : hasRemoteFlow ? nextFlow : null)
				if (recoveredBackup) {
					setSyncStatus(backup!.lastError ? 'failed' : 'local-only')
					setSyncMessage(backup!.lastError || 'Recovered game stats saved on this device only. Other devices cannot see them yet.')
					if (navigator.onLine) {
						setSaveRevision(value => value + 1)
					}
				} else if (backupMatchesRemote && flowBackupKey) {
					removeLocalFlowBackup(flowBackupKey)
				}
			}
		}

		void initialize()

		return () => {
			cancelled = true
		}
	}, [ flow, flowBackupKey, flowIsLoading, game, gameId, hydrateFromFlow, loadLatestFlow ])

	useEffect(() => {
		if (
			!gameId
			|| initializedGameRef.current !== gameId
			|| recordingState.flowData.setup
			|| !hasRecordedFlowData(flow)
		) {
			return
		}

		hydrateFromFlow(flow)
	}, [ flow, gameId, hydrateFromFlow, recordingState.flowData.setup ])

	useEffect(() => {
		if (isDeletingFlow || !game || !gameId || !flowBackupKey || saveRevision === 0 || !recordingState.flowData.setup) {
			return
		}

		const dataToSave = buildPersistedFlowData(recordingState)
		const dataSignature = getFlowSyncSignature(dataToSave)
		saveLocalFlowBackup(flowBackupKey, dataToSave, {
			lastAttemptedAt: Date.now(),
			lastError: null,
		})

		if (!navigator.onLine) {
			setSyncStatus('local-only')
			setSyncMessage('Saved on this device only. Waiting for network.')
			return
		}

		setSyncStatus('saving')
		setSyncMessage(null)

		const timer = setTimeout(() => {
			lastSavedFlowSignatureRef.current = dataSignature
			void saveFlow(gameId, dataToSave)
				.then(() => {
					if (removeLocalFlowBackupIfSignatureMatches(flowBackupKey, dataSignature)) {
						setSyncStatus('idle')
						setSyncMessage(null)
					} else {
						showPendingLocalFlowBackup(flowBackupKey, setSyncStatus, setSyncMessage)
					}
				})
				.catch(error => {
					console.error('Failed to save flow data', error)
					const message = getFlowSaveErrorMessage(error)
					setSyncStatus('failed')
					setSyncMessage(message)
					saveLocalFlowBackup(flowBackupKey, dataToSave, {
						lastAttemptedAt: Date.now(),
						lastError: message,
					})
				})
		}, 500)

		return () => clearTimeout(timer)
	}, [ flowBackupKey, game, gameId, isDeletingFlow, recordingState, saveFlow, saveRevision ])

	useEffect(() => {
		if (!gameId || initializedGameRef.current !== gameId || !flow || !hasRecordedFlowData(flow)) {
			return
		}

		if (syncStatus !== 'idle') {
			return
		}

		if (lastRemoteUpdatedRef.current === null) {
			lastRemoteUpdatedRef.current = flow.updated
			return
		}

		if (flow.updated === lastRemoteUpdatedRef.current) {
			return
		}

		lastRemoteUpdatedRef.current = flow.updated

		if (getFlowSyncSignature(flow) === lastSavedFlowSignatureRef.current) {
			return
		}

		if (recordingState.flowData.setup) {
			setShowConcurrentRecordingWarning(true)
		}
	}, [ flow, gameId, recordingState, syncStatus ])

	useEffect(() => {
		if (!flowBackupKey || !recordingState.flowData.setup) {
			return
		}

		const updateNetworkStatus = () => {
			const backup = loadLocalFlowBackup(flowBackupKey)
			if (!backup) {
				return
			}

			if (!navigator.onLine) {
				setSyncStatus('local-only')
				setSyncMessage('Saved on this device only. Waiting for network.')
			} else if (syncStatus !== 'idle' && syncStatus !== 'saving') {
				setSaveRevision(value => value + 1)
			}
		}

		window.addEventListener('online', updateNetworkStatus)
		window.addEventListener('offline', updateNetworkStatus)

		return () => {
			window.removeEventListener('online', updateNetworkStatus)
			window.removeEventListener('offline', updateNetworkStatus)
		}
	}, [ flowBackupKey, recordingState.flowData.setup, syncStatus ])

	const isSetupViewVisible = !recordingState.flowData.setup || isEditingSetup || openSetupFromRoute || keepSetupOpenForExit

	useEffect(() => {
		if (!isSetupViewVisible || !recordingState.flowData.setup) {
			if (setupEntrySignature !== null) {
				setSetupEntrySignature(null)
			}
			return
		}

		if (setupEntrySignature === null) {
			setSetupEntrySignature(getSetupSignature(setupDraft))
		}
	}, [
		isSetupViewVisible,
		recordingState.flowData.setup,
		setupDraft,
		setupEntrySignature,
	])

	const [ flashClass, setFlashClass ] = useState('')
	const flashTimeout = useRef<ReturnType<typeof setTimeout>>()

	const flash = useCallback((type: 'turnover' | 'goal' | 'super-goal' | 'quarter' | 'undo', color?: string) => {
		const impacts = {
			turnover: ImpactStyle.Light,
			goal: ImpactStyle.Heavy,
			'super-goal': ImpactStyle.Heavy,
			quarter: ImpactStyle.Medium,
			undo: ImpactStyle.Light,
		}
		Haptics.impact({ style: impacts[type] })
		if (type === 'super-goal') {
			setTimeout(() => {
				Haptics.impact({ style: impacts[type] })
			}, 350)
		}
		clearTimeout(flashTimeout.current)
		if (color) {
			document.body.style.setProperty('--record-flows-flash-color', withAlpha(color, type === 'turnover' ? 0.12 : 0.45))
		} else {
			document.body.style.removeProperty('--record-flows-flash-color')
		}
		setFlashClass(`flash-${type}`)
		flashTimeout.current = setTimeout(() => setFlashClass(''), type === 'super-goal' ? 600 : 300)
	}, [])

	if (!gameId) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">No game selected</h1>
						</section>
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	if (gameDoesNotExist) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">This game does not exist.</h1>
						</section>
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	if ((gameIsLoading || flowIsLoading || isInitializingFlow) && initializedGameRef.current !== gameId) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">Loading recording screen...</h1>
						</section>
                        <LoadingSpinner />
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	const { flowData, currentEvent } = recordingState
	const hasSetup = !!flowData.setup
	const isGameComplete = isFlowGameComplete(flowData, numPeriods)
	const score = calculateScore(flowData.events)
	const quarterHasRecordedActivity = hasCurrentQuarterRecordedActivity(flowData, currentEvent)
	const possessionSide = currentEvent ? getPossessionSide(currentEvent) : null
	const centrePassSide = currentEvent?.cpTeamCode || null
	const recordingTeamNames = deriveTeamDisplayNames(team?.name, game?.opponent, 'Home', 'Away')
	const periodTerms = getPeriodDisplayTerms(numPeriods)
	const quarterLabel = isGameComplete ? 'Game Complete' : formatPeriodNumber(flowData.currentQuarter, numPeriods)
	const courtQuarter = currentEvent
		? flowData.currentQuarter
		: Math.max(1, flowData.currentQuarter - 1)
	const canUndoQuarterBreak = flowData.quarterBreaks.length > 0
		&& flowData.quarterBreaks[flowData.quarterBreaks.length - 1] === flowData.events.length
	const canUndo = !!currentEvent?.turnovers.length || flowData.events.length > 0 || canUndoQuarterBreak
	const lastActionType = getLastActionType(flowData, currentEvent)
	const canToggleCentrePass = !!currentEvent
		&& currentEvent.turnovers.length === 0
		&& (lastActionType === 'goal' || lastActionType === 'quarterBreak')
	const courtZones = flowData.setup
		? getCourtZones(flowData.setup.homeAttackDirection, courtQuarter)
		: [ ]
	const blockedTurnoverZones = currentEvent
		? getBlockedTurnoverZones(currentEvent)
		: new Set<FlowEventZone>()
	const lastEventSummary = flowData.setup
		? describeLastEvent({
			flowData,
			currentEvent,
			homeName: recordingTeamNames.home,
			awayName: recordingTeamNames.away,
			numPeriods,
		})
		: null
	const lastCourtMarker = flowData.setup
		? getLastCourtMarker(flowData, currentEvent)
		: null
	const possessionColor = possessionSide && flowData.setup
		? possessionSide === 'home' ? flowData.setup.homeColor : flowData.setup.awayColor
		: '#1e293b'
	const possessionStyle = {
		backgroundColor: possessionColor,
		color: readableTextColor(possessionColor),
	} as React.CSSProperties
	const scoreCardStyle = flowData.setup ? ({
		'--record-flows-home-color': flowData.setup.homeColor,
		'--record-flows-away-color': flowData.setup.awayColor,
	} as React.CSSProperties) : undefined
	const goalButtonColor = currentEvent ? deriveGoalButtonColor(possessionColor) : null
	const goalButtonStyle = currentEvent ? ({
		'--background': goalButtonColor,
		'--background-hover': goalButtonColor,
		'--background-activated': goalButtonColor,
		'--background-focused': goalButtonColor,
		'--color': readableTextColor(goalButtonColor || possessionColor),
	} as React.CSSProperties) : undefined
	const isStartingQuietQuarter = hasSetup && !isGameComplete && !quarterHasRecordedActivity
	const quarterButtonLabel = isStartingQuietQuarter
		? `Starting ${formatPeriodNumber(flowData.currentQuarter, numPeriods)}...`
		: flowData.currentQuarter >= numPeriods ? 'End Game' : `End ${periodTerms.full}`
	const firstCentrePassLocked = hasSetup
		&& (
			flowData.events.length > 0
			|| !!currentEvent
			|| flowData.currentQuarter > 1
			|| flowData.quarterBreaks.length > 0
		)
	const hasExistingFlow = hasRecordedFlowData(flow)
	const canManageExistingFlow = canManageFlowData({
		flow,
		isManager: amViewingOwnTeam,
		userId: flowAuthUid,
		sessionId: flowRecorderSessionId,
	})
	const hasRecordedProgress = hasSetup
	const setupProgressQuarterLabel = formatPeriodNumber(Math.min(flowData.currentQuarter, numPeriods), numPeriods)
	const setupProgressScoreLabel = `${score.home}-${score.away}`
	const shouldShowSetup = isSetupViewVisible
	const hasChangedSetupThisVisit = setupEntrySignature !== null && getSetupSignature(setupDraft) !== setupEntrySignature
	const setupSubmitLabel = hasSetup
        ? hasChangedSetupThisVisit ? 'Apply' : 'Continue'
		: 'Start'
	const concurrentRecordingBanner = showConcurrentRecordingWarning && hasSetup ? (
		<section className="record-flows-card record-flows-sync-warning" role="status">
			Another recorder appears to be saving game stats for this match.
		</section>
	) : null
	const syncIconStatus = isRetryingSave ? 'saving' : syncStatus
	const SyncStatusIcon = syncIconStatus === 'saving'
		? IoCloudUpload
		: syncIconStatus === 'idle'
			? IoCloudDoneSharp
			: IoCloudOfflineSharp
	const syncStatusLabel = getFlowSyncStatusLabel(syncIconStatus, syncMessage)
	const syncIconButton = hasSetup ? (
		<button
			type="button"
			className={`record-flows-cloud-status sync-${syncIconStatus}`}
			onClick={retrySaveFlow}
			aria-label={syncStatusLabel}
			title={syncStatusLabel}
		>
			<SyncStatusIcon aria-hidden="true" />
		</button>
	) : null

	const onStartRecording = async () => {
		if (!hasExistingFlow) {
			const latestFlow = await loadLatestFlow()
			if (hasRecordedFlowData(latestFlow)) {
				hydrateFromFlow(latestFlow)
				presentAlert({
					header: 'Game stats already exist',
					message: 'This game already has recorded stats, so the latest recording has been loaded instead of starting over.',
					buttons: [ 'OK' ],
				})
				return
			}
		}

		setRecordingState(prev => {
			const nextFlowData: EditableFlowData = prev.flowData.setup
				? {
					...prev.flowData,
					setup: setupDraft,
				}
				: {
					...prev.flowData,
					setup: setupDraft,
					currentQuarter: prev.flowData.currentQuarter || 1,
				}
			return {
				flowData: nextFlowData,
				currentEvent: prev.currentEvent || buildFreshCurrentEvent(nextFlowData, numPeriods),
			}
		})
		setIsEditingSetup(false)
		setKeepSetupOpenForExit(false)
		setSetupEntrySignature(null)
		if (openSetupFromRoute && recordFlowUrl) {
			router.push(recordFlowUrl, 'none', 'replace')
		}
		setSaveRevision(value => value + 1)
	}

	const onRecordTurnover = (zone: FlowEventZone) => {
		if (!recordingState.currentEvent) {
			return
		}
		const nextPossessionSide = oppositeSide(getPossessionSide(recordingState.currentEvent))
		const nextPossessionColor = flowData.setup
			? nextPossessionSide === 'home' ? flowData.setup.homeColor : flowData.setup.awayColor
			: undefined
		flash('turnover', nextPossessionColor)
		setRedoStack([])
		setRecordingState(prev => {
			if (!prev.currentEvent) {
				return prev
			}
			return {
				...prev,
				currentEvent: {
					...prev.currentEvent,
					turnovers: [ ...prev.currentEvent.turnovers, zone ],
				},
			}
		})
		setSaveRevision(value => value + 1)
	}

	const onToggleCentrePass = () => {
		if (!canToggleCentrePass) {
			return
		}
		setRedoStack([])
		setRecordingState(prev => {
			if (!prev.currentEvent || prev.currentEvent.turnovers.length > 0) {
				return prev
			}
			return {
				...prev,
				currentEvent: {
					...prev.currentEvent,
					cpTeamCode: oppositeSide(prev.currentEvent.cpTeamCode),
				},
			}
		})
		setSaveRevision(value => value + 1)
	}

	const onOpenSetup = () => {
		const nextSetup = flowData.setup || createDefaultSetup()
		setSetupDraft(nextSetup)
		setSetupEntrySignature(getSetupSignature(nextSetup))
		setIsEditingSetup(true)
	}

	const onRecordResult = (result: FlowEventResult) => {
		if (!recordingState.currentEvent) {
			return
		}
		flash(result === 'GG' ? 'super-goal' : 'goal', possessionColor)
		setRedoStack([])
		setRecordingState(prev => {
			if (!prev.currentEvent) {
				return prev
			}

			const completedEvent: FlowEvent = {
				cpTeamCode: prev.currentEvent.cpTeamCode,
				turnovers: [ ...prev.currentEvent.turnovers ],
				result,
			}
			const nextFlowData: EditableFlowData = {
				...prev.flowData,
				events: [ ...prev.flowData.events, completedEvent ],
			}
			return {
				flowData: nextFlowData,
				currentEvent: buildFreshCurrentEvent(nextFlowData, numPeriods),
			}
		})
		setSaveRevision(value => value + 1)
	}

	const startGoalPress = () => {
		if (!currentEvent) {
			return
		}

		clearSuperGoalTimer()
		goalPressActiveRef.current = true
		superGoalArmedRef.current = false
		setIsSuperGoalArmed(false)
		superGoalTimerRef.current = setTimeout(() => {
			if (!goalPressActiveRef.current) {
				return
			}
			superGoalArmedRef.current = true
			setIsSuperGoalArmed(true)
		}, SUPER_GOAL_HOLD_MS)
	}

	const finishGoalPress = () => {
		if (!goalPressActiveRef.current) {
			return
		}

		const result: FlowEventResult = superGoalArmedRef.current ? 'GG' : 'G'
		resetGoalPress()
		onRecordResult(result)
	}

	const onGoalPointerDown = (event: React.PointerEvent<HTMLElement>) => {
		event.currentTarget.setPointerCapture?.(event.pointerId)
		startGoalPress()
	}

	const onGoalPointerUp = (event: React.PointerEvent<HTMLElement>) => {
		event.currentTarget.releasePointerCapture?.(event.pointerId)
		finishGoalPress()
	}

	const onGoalKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
			event.preventDefault()
			startGoalPress()
		}
	}

	const onGoalKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			finishGoalPress()
		}
	}

	const startEmptyQuarterPress = () => {
		if (!isStartingQuietQuarter) {
			return
		}

		clearEmptyQuarterTimer()
		emptyQuarterPressActiveRef.current = true
		emptyQuarterArmedRef.current = false
		emptyQuarterTimerRef.current = setTimeout(() => {
			if (!emptyQuarterPressActiveRef.current) {
				return
			}
			emptyQuarterArmedRef.current = true
		}, EMPTY_QUARTER_HOLD_MS)
	}

	const finishEmptyQuarterPress = () => {
		if (!emptyQuarterPressActiveRef.current) {
			return
		}

		const shouldEndQuarter = emptyQuarterArmedRef.current
		resetEmptyQuarterPress()
		if (shouldEndQuarter) {
			void onEndQuarter()
		}
	}

	const onQuarterPointerDown = (event: React.PointerEvent<HTMLElement>) => {
		if (!isStartingQuietQuarter) {
			return
		}

		event.currentTarget.setPointerCapture?.(event.pointerId)
		startEmptyQuarterPress()
	}

	const onQuarterPointerUp = (event: React.PointerEvent<HTMLElement>) => {
		if (!isStartingQuietQuarter) {
			return
		}

		event.currentTarget.releasePointerCapture?.(event.pointerId)
		finishEmptyQuarterPress()
	}

	const onQuarterClick = () => {
		if (isStartingQuietQuarter) {
			return
		}

		void onEndQuarter()
	}

	const onEndQuarter = async () => {
		if (!recordingState.flowData.setup || recordingState.flowData.currentQuarter > numPeriods) {
			return
		}

		const includeCurrentEvent = shouldAskAboutCentrePassAfterGoal({
			flowData: recordingState.flowData,
			currentEvent: recordingState.currentEvent,
		})
			? await confirmCentrePassTakenAfterLastGoal()
			: !!recordingState.currentEvent

		flash('quarter')
		setRedoStack([])
		setRecordingState(prev => {
			if (!prev.flowData.setup || prev.flowData.currentQuarter > numPeriods) {
				return prev
			}

			const nextEvents = [ ...prev.flowData.events ]
			if (includeCurrentEvent && prev.currentEvent) {
				nextEvents.push(endIncompleteEvent(prev.currentEvent))
			}

			const nextFlowData: EditableFlowData = {
				...prev.flowData,
				events: nextEvents,
				quarterBreaks: [ ...prev.flowData.quarterBreaks, nextEvents.length ],
				currentQuarter: prev.flowData.currentQuarter + 1,
			}

			return {
				flowData: nextFlowData,
				currentEvent: buildFreshCurrentEvent(nextFlowData, numPeriods),
			}
		})
		setSaveRevision(value => value + 1)
	}

	const confirmCentrePassTakenAfterLastGoal = () => new Promise<boolean>(resolve => {
		presentAlert({
			header: 'Was a Centre Pass taken after the last goal?',
			backdropDismiss: false,
			buttons: [
				{
					text: 'No',
					role: 'cancel',
					handler: () => {
						resolve(false)
					},
				},
				{
					text: 'Yes',
					handler: () => {
						resolve(true)
					},
				},
			],
		})
	})

	const onUndo = () => {
		if (!canUndo) {
			return
		}
		flash('undo')
		setRedoStack(stack => [ ...stack, recordingState ])
		setRecordingState(prev => {
			if (prev.currentEvent?.turnovers.length) {
				return {
					...prev,
					currentEvent: {
						...prev.currentEvent,
						turnovers: prev.currentEvent.turnovers.slice(0, -1),
					},
				}
			}

			const canReopenQuarter = prev.flowData.quarterBreaks.length > 0
				&& prev.flowData.quarterBreaks[prev.flowData.quarterBreaks.length - 1] === prev.flowData.events.length
				&& prev.flowData.currentQuarter > 1

			if (canReopenQuarter) {
					const lastEvent = prev.flowData.events[prev.flowData.events.length - 1]
					const nextFlowData: EditableFlowData = {
						...prev.flowData,
						currentQuarter: prev.flowData.currentQuarter - 1,
						quarterBreaks: prev.flowData.quarterBreaks.slice(0, -1),
						events: lastEvent?.result === 'X'
							? prev.flowData.events.slice(0, -1)
							: prev.flowData.events,
					}
					return {
						flowData: nextFlowData,
						currentEvent: lastEvent?.result === 'X'
							? {
								cpTeamCode: lastEvent.cpTeamCode,
								turnovers: [ ...lastEvent.turnovers ],
							}
							: buildFreshCurrentEvent(nextFlowData, numPeriods),
				}
			}

			const lastEvent = prev.flowData.events[prev.flowData.events.length - 1]
			if (!lastEvent) {
				return prev
			}

			return {
				flowData: {
					...prev.flowData,
					events: prev.flowData.events.slice(0, -1),
				},
				currentEvent: {
					cpTeamCode: lastEvent.cpTeamCode,
					turnovers: [ ...lastEvent.turnovers ],
				},
			}
		})
		setSaveRevision(value => value + 1)
	}

	const onRedo = () => {
		if (!redoStack.length) {
			return
		}
		flash('undo')
		const restored = redoStack[redoStack.length - 1]
		setRedoStack(stack => stack.slice(0, -1))
		setRecordingState(restored)
		setSaveRevision(value => value + 1)
	}

	const clearRecordedProgress = () => {
		initializedGameRef.current = gameId || null
		lastRemoteUpdatedRef.current = null
		lastSavedFlowSignatureRef.current = null
		if (flowBackupKey) {
			removeLocalFlowBackup(flowBackupKey)
		}
		setRecordingState(createRecordingState(gameId, null, numPeriods))
		setSetupDraft(createDefaultSetup())
		setSetupEntrySignature(null)
		setSaveRevision(0)
		setIsEditingSetup(false)
		setRedoStack([])
		setShowConcurrentRecordingWarning(false)
		setSyncStatus('idle')
		setSyncMessage(null)
	}

	const onDeleteRecordedData = () => {
		if (!gameId || isDeletingFlow) {
			return
		}

		const matchLabel = `${team?.name || 'Home'} vs ${game?.opponent || 'Away'}`

		const ownershipWarning = needsFlowDeleteOwnershipWarning({
			flow,
			userId: flowAuthUid,
		}) ? ' These stats were recorded by somebody else.' : ''

		presentAlert({
			header: 'Delete recorded game stats?',
			subHeader: ownershipWarning ? 'These stats were recorded by another user' : undefined,
			message: `This will PERMANENTLY delete all recorded flow map stats for "${matchLabel}".${ownershipWarning} Are you sure?`,
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Delete and start over',
					role: 'destructive',
					handler: () => {
						setIsDeletingFlow(true)
						void (async () => {
							try {
								let latestFlow: FlowData | null = null
								try {
									latestFlow = await loadLatestFlow({ throwOnError: true })
								} catch (error) {
									if (flowBackupKey && !navigator.onLine && !hasRecordedFlowData(flow)) {
										console.warn('Could not check remote flow before clearing local flow data', error)
									} else {
										throw error
									}
								}

								if (hasRecordedFlowData(latestFlow)) {
									await deleteFlow(gameId)
								} else if (game?.hasFlowMap) {
									await clearGameFlowMapFlag(gameId)
								}
								clearRecordedProgress()
							} catch (error) {
								console.error('Failed to delete flow data', error)
								presentAlert({
									header: 'Delete failed',
									message: 'The recorded game stats could not be deleted. Please try again.',
									buttons: [ 'OK' ],
								})
							} finally {
								setIsDeletingFlow(false)
							}
						})()
					},
				},
			],
		})
	}

	const onTidyFlowData = (tidiedFlow: FlowData) => {
		setRecordingState({
			flowData: tidiedFlow,
			currentEvent: null,
		})
		setRedoStack([])
		setSaveRevision(value => value + 1)
	}

	const onInviteUser = () => {
		if (!gameId) {
			return
		}

		if (!userHasPro) {
			if (!flowInviteUrl) {
				return
			}
			router.push(flowInviteUrl, 'forward', 'push')
			return
		}

		const shareLink = getGameSharePreviewUrl({
			kind: 'record-flows',
			gameId,
			section,
		})
		const shareTitle = `Record game stats for ${game?.opponent || 'this game'}`

		void shareUrl({
			url: shareLink,
			title: shareTitle,
			presentAlert,
		})
	}

	const onView = () => {
		if (!gameId) {
			return
		}

		const url = getUrl({
			relative: true,
			page: `games/${gameId}/flow-map`,
			section,
		})
		pushOrGoBackToPreviousPage(router, url)
	}

	const onViewRoster = () => {
		if (!gameId) {
			return
		}

		const url = getUrl({
			relative: true,
			page: `games/${gameId}`,
			section,
		})
		pushOrGoBackToPreviousPage(router, url)
	}

	if (hasExistingFlow && !canManageExistingFlow) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">Game stats already exist for this game.</h1>
							<p>
								Only a team coach/manager or the person who started this recording can continue or delete it.
							</p>
						</section>
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	if (!hasExistingFlow && (isCheckingStartAccess || !currentTeamId || startAccessTeamId !== currentTeamId || canStartNewFlow === null)) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">Checking recording access...</h1>
                            <LoadingSpinner />
						</section>
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	if (!hasExistingFlow && !canStartNewFlow) {
		return (
			<IonPage>
				<IonContent>
					<div className="record-flows-page">
						<section className="record-flows-card">
							<div className="record-flows-eyebrow">Record Flows</div>
							<h1 className="record-flows-title">Opening flow invite...</h1>
						</section>
                        <LoadingSpinner />
					</div>
				</IonContent>
				<Header backHref={backHref} />
			</IonPage>
		)
	}

	return (
		<IonPage>
			<IonContent>
				<div className="record-flows-page">
					{concurrentRecordingBanner}
					{
						shouldShowSetup ? (
							<>
								<section className="flow-card record-flows-match-card">
									<div className="flow-card-body flow-setup-card-body">
										<IonIcon icon={recordingOutline} size="large" />
										<h1 className="record-flows-setup-title">
											{game?.round ? labelRound(game.round) : 'Game'}
                                            {' '}
                                            v
                                            {' '}
                                            {game?.opponent || 'Opponent'}
										</h1>
										<div className="flow-match-meta">
											{game?.date && <span>{formatDate(game.date, 'long')}</span>}
											{game?.venue && <span>{game.venue}</span>}
										</div>
									</div>
								</section>
								<SetupView
									setupDraft={setupDraft}
									setSetupDraft={setSetupDraft}
									courtQuarter={courtQuarter}
									numPeriods={numPeriods}
									homeName={team?.name || 'Home'}
									awayName={game?.opponent || 'Away'}
									onStartRecording={onStartRecording}
									submitLabel={setupSubmitLabel}
									disableFirstCentrePass={firstCentrePassLocked}
									progressSummary={hasRecordedProgress ? {
										quarterLabel: setupProgressQuarterLabel,
										scoreLabel: setupProgressScoreLabel,
										isComplete: isGameComplete,
									} : null}
									onDeleteAndStartOver={hasRecordedProgress ? onDeleteRecordedData : undefined}
									isDeletingProgress={isDeletingFlow}
									tidyControl={isGameComplete && game?.result && flowData.setup ? (
										<TidyFlowMap
											flowData={flowData as FlowData}
											result={game.result}
											numPeriods={numPeriods}
											homeName={team?.name || 'Home'}
											awayName={game.opponent || 'Away'}
											disabled={isDeletingFlow}
											onChange={onTidyFlowData}
										/>
									) : null}
									onInviteUser={onInviteUser}
									onViewRoster={onViewRoster}
									onView={onView}
									onExport={exportFlowJson}
									onImport={importFlowJson}
									onTutorial={() => setIsHelpOpen(true)}
								/>
							</>
						) : (
							<section className={`record-flows-shell${flashClass ? ` ${flashClass}` : ''}`}>
								<div className="record-flows-topbar">
									<div className="record-flows-card record-flows-stat-card">
										<div className="record-flows-stat-label">{periodTerms.full}</div>
                                        <div className="record-flows-stat-value">{quarterLabel}</div>
                                        {syncIconButton}
									</div>
									<div className="record-flows-card record-flows-score-card" style={scoreCardStyle}>
										<div className="record-flows-score-team home">
											<span className="record-flows-score-name">{team?.name || 'Home'}</span>
											<strong>{score.home}</strong>
										</div>
										<div className="record-flows-score-separator">-</div>
										<div className="record-flows-score-team away">
											<span className="record-flows-score-name">{game?.opponent || 'Away'}</span>
											<strong>{score.away}</strong>
										</div>
									</div>
								</div>

								<section className="record-flows-card record-flows-possession-card" style={possessionStyle}>
									<div className="record-flows-possession-label">Possession</div>
									<div className="record-flows-possession-team">
										{possessionSide ? labelSide(possessionSide, recordingTeamNames.home, recordingTeamNames.away) : 'Game Complete'}
									</div>
									<div className="record-flows-status">
										<span className="record-flows-status-prefix">Centre Pass:</span>
										<span className="record-flows-status-team">
											{centrePassSide ? labelSide(centrePassSide, recordingTeamNames.home, recordingTeamNames.away) : 'None'}
										</span>
										<button
											type="button"
											className={`record-flows-toggle-cp${canToggleCentrePass ? ' visible' : ''}`}
											onClick={onToggleCentrePass}
											disabled={!canToggleCentrePass}
										>
											Change
										</button>
									</div>
								</section>

								<div className="record-flows-primary-actions">
									<IonButton
										expand="block"
										size="large"
										fill="outline"
										className={`record-flows-action action-quarter${isStartingQuietQuarter ? ' starting-quarter' : ''}`}
										onClick={onQuarterClick}
										onPointerDown={onQuarterPointerDown}
										onPointerUp={onQuarterPointerUp}
										onPointerCancel={resetEmptyQuarterPress}
										onPointerLeave={resetEmptyQuarterPress}
										aria-disabled={isStartingQuietQuarter}
										disabled={!hasSetup || isGameComplete}
									>
										{quarterButtonLabel}
									</IonButton>
									<IonButton
										expand="block"
										size="large"
										className={`record-flows-action action-goal${isSuperGoalArmed ? ' super-goal-armed' : ''}`}
										onPointerDown={onGoalPointerDown}
										onPointerUp={onGoalPointerUp}
										onPointerCancel={resetGoalPress}
										onKeyDown={onGoalKeyDown}
										onKeyUp={onGoalKeyUp}
										disabled={!currentEvent}
										style={goalButtonStyle}
									>
										{isSuperGoalArmed ? 'Super Goal' : 'Goal'}
									</IonButton>
								</div>

								<section className="record-flows-card record-flows-court-card">
									<div className="record-flows-section-label">Tap Zone to Record Turnover</div>
                                    {
                                        flowData.setup && (
                                            <TurnoverCourt
                                                zones={courtZones}
                                                homeAttackDirection={homeAttackDirectionForQuarter(
                                                    flowData.setup.homeAttackDirection,
                                                    courtQuarter,
                                                )}
                                                homeColor={flowData.setup.homeColor}
                                                awayColor={flowData.setup.awayColor}
                                                homeName={recordingTeamNames.home}
                                                awayName={recordingTeamNames.away}
                                                lastMarker={lastCourtMarker}
                                                onSelectZone={onRecordTurnover}
                                                blockedZones={blockedTurnoverZones}
                                                disabled={!currentEvent}
                                            />
                                        )
                                    }
                                </section>

                                <section className="record-flows-card record-flows-last-event-card">
                                    <div className="record-flows-section-label">Last Event</div>
                                    <div className="record-flows-last-event-copy">
                                        {lastEventSummary || 'No events recorded yet.'}
                                    </div>
                                </section>

                                <div className="record-flows-bottom-actions">
                                    <IonButton
                                        expand="block"
                                        size="large"
                                        fill="outline"
                                        className="record-flows-action action-undo"
                                        onClick={onUndo}
                                        disabled={!canUndo}
                                    >
                                        <span className="record-flows-action-icon">
                                            <FaUndo />
                                        </span>
                                        Undo
                                    </IonButton>
                                    <IonButton
                                        expand="block"
                                        size="large"
                                        fill="clear"
                                        className="record-flows-action action-redo"
                                        onClick={onRedo}
                                        disabled={!redoStack.length}
                                    >
                                        <span className="record-flows-action-icon">
                                            <FaRedo />
                                        </span>
                                        Redo
                                    </IonButton>
                                    <IonButton
                                        expand="block"
                                        size="large"
                                        fill="clear"
                                        className="record-flows-action action-setup"
                                        onClick={onOpenSetup}
                                    >
                                        <IonIcon icon={settingsSharp} slot="start" />
                                        Setup
                                    </IonButton>
                                </div>

                                <RecordFlowsExtraLinks
									onViewRoster={onViewRoster}
                                    onView={onView}
                                />
                            </section>
                        )
                    }
				</div>
			</IonContent>
			<Header backHref={backHref} />
			<TutorialRecordFlowsModal
				isOpen={isHelpOpen}
				onDismiss={() => setIsHelpOpen(false)}
			/>
		</IonPage>
	)
}

const SetupView = (props: {
	setupDraft: FlowData['setup']
	setSetupDraft: React.Dispatch<React.SetStateAction<FlowData['setup']>>
	courtQuarter: number
	numPeriods: number
	homeName: string
	awayName: string
	onStartRecording: () => void
	submitLabel: string
	disableFirstCentrePass: boolean
	progressSummary?: {
		quarterLabel: string
		scoreLabel: string
		isComplete: boolean
	} | null
	onDeleteAndStartOver?: () => void
	isDeletingProgress?: boolean
	tidyControl?: React.ReactNode
	onInviteUser: () => void
	onViewRoster: () => void
	onView: () => void
	onExport: () => void
	onImport: () => void
	onTutorial: () => void
}) => {
	const {
		setupDraft,
		setSetupDraft,
		courtQuarter,
		numPeriods,
		homeName,
		awayName,
		onStartRecording,
		submitLabel,
		disableFirstCentrePass,
		progressSummary,
		onDeleteAndStartOver,
		isDeletingProgress,
		tidyControl,
		onInviteUser,
		onViewRoster,
		onView,
		onExport,
		onImport,
		onTutorial,
	} = props
	const colorsConflict = setupDraft.homeColor === setupDraft.awayColor
	// Keep the draft in the saved Q1 orientation so reopening Setup cannot flip
	// it. Only the direction buttons use the quarter currently shown on court.
	const currentAttackDirection = homeAttackDirectionForQuarter(setupDraft.homeAttackDirection, courtQuarter)

	return (
		<section className="record-flows-card">
			<div className="record-flows-section-label">Setup</div>

			{
				progressSummary && onDeleteAndStartOver && (
					<div className="record-flows-setup-progress">
						<span className="record-flows-setup-progress-copy">
							{
								progressSummary.isComplete
									? `Game has recorded data (${progressSummary.scoreLabel}).`
									: `Data recording in progress (${progressSummary.quarterLabel}: ${progressSummary.scoreLabel}).`
							}
						</span>
						<button
							type="button"
							className="record-flows-setup-reset"
							onClick={onDeleteAndStartOver}
							disabled={isDeletingProgress}
						>
							{isDeletingProgress ? 'Deleting...' : 'Delete and start over'}
						</button>
						{progressSummary.isComplete && tidyControl}
					</div>
				)
			}

			<div className={`record-flows-setup-group${disableFirstCentrePass ? ' disabled' : ''}`}>
				<h2>Who has the first centre pass?</h2>
				<div className="record-flows-choice-grid two-up">
					<ChoiceButton
						label={homeName}
						isActive={setupDraft.firstCpTeam === 'home'}
						disabled={disableFirstCentrePass}
						onClick={() => setSetupDraft(prev => ({
							...prev,
							firstCpTeam: 'home',
						}))}
					/>
					<ChoiceButton
						label={awayName}
						isActive={setupDraft.firstCpTeam === 'away'}
						disabled={disableFirstCentrePass}
						onClick={() => setSetupDraft(prev => ({
							...prev,
							firstCpTeam: 'away',
						}))}
					/>
				</div>
			</div>

			<div className="record-flows-setup-group">
				<h2>Which direction are {homeName} attacking in {formatPeriodNumber(courtQuarter, numPeriods)}?</h2>
				<div className="record-flows-choice-grid two-up">
					<ChoiceButton
						label="← Left"
						isActive={currentAttackDirection === 'left'}
						onClick={() => setSetupDraft(prev => ({
							...prev,
							homeAttackDirection: baseHomeAttackDirectionForQuarter('left', courtQuarter),
						}))}
					/>
					<ChoiceButton
						label="Right →"
						isActive={currentAttackDirection === 'right'}
						onClick={() => setSetupDraft(prev => ({
							...prev,
							homeAttackDirection: baseHomeAttackDirectionForQuarter('right', courtQuarter),
						}))}
					/>
				</div>
			</div>

			<div className="record-flows-setup-group">
                <h2>Colour: {homeName}</h2>
				<ColorPicker
					value={setupDraft.homeColor}
					blockedColor={setupDraft.awayColor}
					onChange={color => setSetupDraft(prev => ({
						...prev,
						homeColor: color,
					}))}
				/>
			</div>

			<div className="record-flows-setup-group">
                <h2>Colour: {awayName}</h2>
				<ColorPicker
					value={setupDraft.awayColor}
					blockedColor={setupDraft.homeColor}
					onChange={color => setSetupDraft(prev => ({
						...prev,
						awayColor: color,
					}))}
				/>
			</div>

			{
				colorsConflict && (
					<div className="record-flows-setup-warning">
						Choose different colors for each team.
					</div>
				)
			}

			<IonButton className="record-start-button" expand="block" size="large" onClick={onStartRecording} disabled={colorsConflict || isDeletingProgress}>
				{submitLabel}
			</IonButton>
			<IonButton expand="block" fill="clear" onClick={onInviteUser} disabled={isDeletingProgress}>
				<IonIcon icon={recordingOutline} slot="start" /> Invite User to Record
			</IonButton>
			<RecordFlowsExtraLinks
				disabled={isDeletingProgress}
				onViewRoster={onViewRoster}
				onView={onView}
				onExport={progressSummary ? onExport : undefined}
				onImport={!progressSummary ? onImport : undefined}
				onTutorial={onTutorial}
			/>
		</section>
	)
}

const ChoiceButton = (props: {
	label: string
	isActive: boolean
	disabled?: boolean
	onClick: () => void
}) => (
	<button
		type="button"
		className={`record-flows-choice${props.isActive ? ' active' : ''}`}
		onClick={props.onClick}
		disabled={props.disabled}
	>
		{props.label}
	</button>
)

const ColorPicker = (props: {
	value: string
	blockedColor?: string
	onChange: (color: string) => void
}) => (
	<div className="record-flows-color-grid">
		{
			FLOW_COLOR_PRESETS.map(color => {
				const isBlocked = color === props.blockedColor && color !== props.value
				return (
					<button
					key={color}
					type="button"
					className={`record-flows-color-swatch${props.value === color ? ' active' : ''}${isBlocked ? ' disabled' : ''}`}
					style={{ backgroundColor: color }}
					onClick={() => props.onChange(color)}
					aria-label={`Pick color ${color}`}
					disabled={isBlocked}
					/>
				)
			})
		}
	</div>
)

const TurnoverCourt = (props: {
	zones: FlowEventZone[]
	homeAttackDirection: FlowAttackDirection
	homeColor: string
	awayColor: string
	homeName: string
	awayName: string
	lastMarker: CourtMarker | null
	onSelectZone: (zone: FlowEventZone) => void
	blockedZones: Set<FlowEventZone>
	disabled: boolean
}) => {
	const { zones, homeAttackDirection, homeColor, awayColor, homeName, awayName, lastMarker, onSelectZone, blockedZones, disabled } = props
	const leftAttackSide = homeAttackDirection === 'left' ? 'home' : 'away'
	const rightAttackSide = oppositeSide(leftAttackSide)
	const courtLeft = 0
	const courtTop = 0
	const courtWidth = 900
	const courtHeight = 450
	const thirdWidth = courtWidth / 3
	const courtRight = courtLeft + courtWidth
	const centreX = courtLeft + (courtWidth / 2)
	const centreY = courtTop + (courtHeight / 2)
	const goalCircleRadius = (courtHeight * 4.9) / 15.25
	const centreCircleRadius = (courtHeight * 0.45) / 15.25
	const leftGoalArcPath = describeGoalArc({
		x: courtLeft,
		y: centreY,
		radius: goalCircleRadius,
		isLeftSide: true,
	})
	const rightGoalArcPath = describeGoalArc({
		x: courtRight,
		y: centreY,
		radius: goalCircleRadius,
		isLeftSide: false,
	})
	const endFills = {
		left: withAlpha(leftAttackSide === 'home' ? homeColor : awayColor, 0.2),
		right: withAlpha(rightAttackSide === 'home' ? homeColor : awayColor, 0.2),
	}
	const leftAttackLabel = `ATTACK\n${leftAttackSide === 'home' ? homeName : awayName}`
	const rightAttackLabel = `ATTACK\n${rightAttackSide === 'home' ? homeName : awayName}`
	const cpMarkerSide = lastMarker?.type === 'goal'
		? lastMarker.cpSide
		: lastMarker?.side || null
	const markerColor = lastMarker
		? (lastMarker.side === 'home' ? homeColor : awayColor)
		: null
	const cpMarkerColor = cpMarkerSide
		? (cpMarkerSide === 'home' ? homeColor : awayColor)
		: null
	const markerDirection = cpMarkerSide
		? teamAttackDirection(homeAttackDirection, cpMarkerSide)
		: null
	const cpArrow = markerDirection
		? directionalArrowFromBase({
			x: centreX,
			y: centreY,
			direction: markerDirection,
			length: 46,
			headSize: 18,
		})
		: null
	const turnoverZoneIndex = lastMarker?.type === 'turnover'
		? zones.indexOf(lastMarker.zone)
		: -1
	const turnoverZoneX = turnoverZoneIndex >= 0
		? (thirdWidth * turnoverZoneIndex) + (thirdWidth / 2)
		: centreX
	const turnoverArrowY = lastMarker?.side === 'home'
		? courtTop + (courtHeight * 0.38)
		: courtTop + (courtHeight * 0.62)
	const turnoverArrow = markerDirection
		? directionalArrow({
			x: turnoverZoneX,
			y: turnoverArrowY,
			direction: markerDirection,
			length: 84,
			headSize: 18,
		})
		: null
	const goalDirection = lastMarker
		? teamAttackDirection(homeAttackDirection, lastMarker.side)
		: null
	const goalMarkerX = goalDirection === 'left' ? 26 : courtRight - 26

	return (
		<div className={`record-flows-court${disabled ? ' disabled' : ''}`}>
			<div className="record-flows-court-surface">
				<svg className="record-flows-court-svg" viewBox="0 0 900 450" preserveAspectRatio="none" aria-hidden="true">
					<rect x="0" y="0" width="900" height="450" className="record-flows-court-bg" />
					<rect x={courtLeft} y={courtTop} width={thirdWidth} height={courtHeight} fill={endFills.left} />
					<rect x={courtLeft + (thirdWidth * 2)} y={courtTop} width={thirdWidth} height={courtHeight} fill={endFills.right} />
					<rect
						x={courtLeft}
						y={courtTop}
						width={courtWidth}
						height={courtHeight}
						className="record-flows-court-outline"
					/>
					<line
						x1={courtLeft + thirdWidth}
						y1={courtTop}
						x2={courtLeft + thirdWidth}
						y2={courtTop + courtHeight}
						className="record-flows-court-line"
					/>
					<line
						x1={courtLeft + (thirdWidth * 2)}
						y1={courtTop}
						x2={courtLeft + (thirdWidth * 2)}
						y2={courtTop + courtHeight}
						className="record-flows-court-line"
					/>
					<circle cx={centreX} cy={centreY} r={centreCircleRadius} className="record-flows-court-circle" />
					<path d={leftGoalArcPath} className="record-flows-court-line" />
					<path d={rightGoalArcPath} className="record-flows-court-line" />
					{
						lastMarker && markerColor && (
							<g className="record-flows-last-marker">
								{
									(lastMarker.type === 'cp' || lastMarker.type === 'goal') && cpArrow && cpMarkerColor && (
										<>
											<line
												x1={cpArrow.line.x1}
												y1={cpArrow.line.y1}
												x2={cpArrow.line.x2}
												y2={cpArrow.line.y2}
												stroke={cpMarkerColor}
												strokeWidth="8"
												strokeLinecap="round"
											/>
											<polygon points={cpArrow.headPoints} fill={cpMarkerColor} />
										</>
									)
								}
								{
									lastMarker.type === 'turnover' && turnoverArrow && (
										<>
											<line
												x1={turnoverArrow.line.x1}
												y1={turnoverArrow.line.y1}
												x2={turnoverArrow.line.x2}
												y2={turnoverArrow.line.y2}
												stroke={markerColor}
												strokeWidth="8"
												strokeLinecap="round"
											/>
											<polygon points={turnoverArrow.headPoints} fill={markerColor} />
										</>
									)
								}
								{
									lastMarker.type === 'goal' && (
										<circle
											cx={goalMarkerX}
											cy={centreY}
											r="16"
											fill={markerColor}
											stroke="rgb(248 250 252 / 0.96)"
											strokeWidth="3"
										/>
									)
								}
							</g>
						)
					}
				</svg>
				<div className="record-flows-court-labels" aria-hidden="true">
					<div className="record-flows-court-third-label">{leftAttackLabel}</div>
					<div className="record-flows-court-third-label mid">MIDCOURT</div>
					<div className="record-flows-court-third-label">{rightAttackLabel}</div>
				</div>
				{
					zones.map((zone, index) => {
						const isBlocked = blockedZones.has(zone)
						return (
							<button
								key={`tap-${zone}-${index}`}
								type="button"
								className={`record-flows-court-hitzone zone-${index}${isBlocked ? ' blocked' : ''}`}
								onClick={() => onSelectZone(zone)}
								disabled={disabled || isBlocked}
								aria-label={`Record turnover in ${index === 0 ? leftAttackLabel.replace('\n', ' ') : index === 1 ? 'MIDCOURT' : rightAttackLabel.replace('\n', ' ')}${isBlocked ? ' (not available here)' : ''}`}
								data-zone={zone}
							/>
						)
					})
				}
			</div>
		</div>
	)
}

const RecordFlowsExtraLinks = (props: {
	disabled?: boolean
	onViewRoster?: () => void
	onView?: () => void
	onExport?: () => void
	onImport?: () => void
	onTutorial?: () => void
}) => (
	<div className="record-flows-extra-links">
		<IonButton
			className="record-flows-help-link"
			fill="clear"
			disabled={props.disabled}
			onClick={props.onTutorial}
		>
			<IonIcon icon={helpCircleOutline} slot="start" />
			How to Record
		</IonButton>
		{
			props.onExport ? (
				<IonButton
					className="record-flows-transfer-link"
					fill="clear"
					disabled={props.disabled}
					onClick={props.onExport}
				>
					<BsCloudDownload className="record-flows-react-icon" aria-hidden="true" />
					Export
				</IonButton>
			) : props.onImport ? (
				<IonButton
					className="record-flows-transfer-link"
					fill="clear"
					disabled={props.disabled}
					onClick={props.onImport}
				>
					<BsCloudArrowUp className="record-flows-react-icon" aria-hidden="true" />
					Import
				</IonButton>
			) : null
		}
		<IonButton
			className="record-flows-roster-link"
			fill="clear"
			disabled={props.disabled}
			onClick={props.onViewRoster}
		>
			<span slot="start" className="record-flows-roster-icon" aria-hidden="true">
				<Icon />
			</span>
			Roster
		</IonButton>
		<IonButton
			className="record-flows-dashboard-link"
			fill="clear"
			disabled={props.disabled}
			onClick={props.onView}
		>
			<GameInsightIcon slot="start" />
			Dashboard
		</IonButton>
	</div>
)

function createDefaultSetup(): FlowData['setup'] {
	return {
		firstCpTeam: 'home',
		homeAttackDirection: 'right',
		homeColor: DEFAULT_HOME_COLOR,
		awayColor: DEFAULT_AWAY_COLOR,
	}
}

function getSetupSignature(setup: FlowData['setup']) {
	return JSON.stringify([
		setup.firstCpTeam,
		setup.homeAttackDirection,
		setup.homeColor,
		setup.awayColor,
	])
}

function createRecordingState(
	gameId: GameId | undefined,
	flow: FlowData | null,
	numPeriods: number,
): RecordingState {
	const flowData = normalizeFlowData(gameId, flow)
	const liveEvent = extractLiveDraftEvent(flow)
	return {
		flowData,
		currentEvent: flowData.setup
			? (
				liveEvent
					? {
						cpTeamCode: liveEvent.cpTeamCode,
						turnovers: [ ...liveEvent.turnovers ],
					}
					: buildFreshCurrentEvent(flowData, numPeriods)
			)
			: null,
	}
}

function buildPersistedFlowData(recordingState: RecordingState): FlowData {
	const persistedEvents = recordingState.currentEvent
		? [
			...recordingState.flowData.events,
			{
				cpTeamCode: recordingState.currentEvent.cpTeamCode,
				turnovers: [ ...recordingState.currentEvent.turnovers ],
				result: 'I' as const,
			},
		]
		: recordingState.flowData.events

	return {
		...recordingState.flowData,
		schemaVersion: recordingState.flowData.schemaVersion ?? FLOW_DATA_SCHEMA_VERSION,
		setup: recordingState.flowData.setup!,
		events: persistedEvents,
		draftEvent: null,
	}
}

function getFlowSyncSignature(flow: FlowData | EditableFlowData | null) {
	if (!flow) {
		return null
	}

	return getFlowDataSignature(flow as FlowData)
}

function countRecordedGoals(flow: FlowData | null | undefined) {
	return (flow?.events || [ ]).filter(event => event.result === 'G' || event.result === 'GG').length
}

function shouldRecoverLocalFlowBackup(
	candidate: FlowData,
	baseline: FlowData | null | undefined,
	candidateSignature: string,
	baselineSignature: string | null,
) {
	if (!hasRecordedFlowData(baseline)) {
		return true
	}

	const candidateGoalCount = countRecordedGoals(candidate)
	const baselineGoalCount = countRecordedGoals(baseline)
	if (candidateGoalCount !== baselineGoalCount) {
		return candidateGoalCount > baselineGoalCount
	}

	// Flow recording is designed around one active stats recorder. If the local
	// backup and remote data have the same score but different details, local is
	// the best signal we have for unsynced work from this device.
	return candidateSignature !== baselineSignature
}

function removeLocalFlowBackupIfSignatureMatches(key: FlowLocalBackupKey, signature: string | null) {
	const backup = loadLocalFlowBackup(key)
	if (!backup || backup.signature === signature) {
		removeLocalFlowBackup(key)
		return true
	}
	return false
}

function showPendingLocalFlowBackup(
	key: FlowLocalBackupKey,
	setSyncStatus: React.Dispatch<React.SetStateAction<FlowSyncStatus>>,
	setSyncMessage: React.Dispatch<React.SetStateAction<string | null>>,
) {
	const backup = loadLocalFlowBackup(key)
	setSyncStatus(backup?.lastError ? 'failed' : 'local-only')
	setSyncMessage(backup?.lastError || 'Saved on this device only. Waiting to sync.')
}

function getFlowSaveErrorMessage(error: unknown) {
	const code = typeof error === 'object' && error && 'code' in error
		? String((error as { code?: unknown }).code)
		: ''

	if (code === 'auth/admin-restricted-operation') {
		return 'Recording is saved on this device, but could not sync because anonymous sign-in is disabled. Sign in, then retry.'
	}

	if (code === 'flow-auth-mismatch') {
		return 'Recording is saved on this device, but sign-in needs refreshing before it can sync.'
	}

	if (code === 'permission-denied') {
		return 'Recording is saved on this device, but Firestore rejected the sync. Refresh sign-in, then retry.'
	}

	return 'Recording is saved on this device, but could not sync. Check your connection, then retry.'
}

function getFlowSyncStatusLabel(status: FlowSyncStatus, message: string | null) {
	if (status === 'saving') {
		return 'Saving game stats.'
	}

	if (status === 'idle') {
		return 'Synced. Other devices can see these stats.'
	}

	return message || 'Saved on this device. Other devices cannot see these stats yet.'
}

function needsFlowDeleteOwnershipWarning(props: {
	flow: FlowData | null | undefined
	userId?: UserId | null
}) {
	const { flow, userId } = props

	if (flow && hasRecordedFlowData(flow) && flow.authorUid && flow.authorUid !== userId) {
        return true
	}

	return false
}

function normalizeFlowData(gameId: GameId | undefined, flow: FlowData | null): EditableFlowData {
	const persistedEvents = stripLiveEvent(flow?.events)
	const liveEvent = extractLiveDraftEvent(flow)
	return {
		id: gameId || flow?.id || '',
		schemaVersion: flow?.schemaVersion ?? FLOW_DATA_SCHEMA_VERSION,
		created: flow?.created || Date.now(),
		updated: flow?.updated || Date.now(),
		authorUid: flow?.authorUid || null,
		authorSessionId: flow?.authorSessionId || null,
		setup: flow?.setup,
		currentQuarter: Math.max(flow?.currentQuarter || 1, 1),
		quarterBreaks: flow?.quarterBreaks ? [ ...flow.quarterBreaks ] : [ ],
		events: persistedEvents
			? persistedEvents.map(event => ({
				cpTeamCode: event.cpTeamCode,
				turnovers: [ ...event.turnovers ],
				result: event.result,
			}))
			: [ ],
		draftEvent: liveEvent
			? {
				cpTeamCode: liveEvent.cpTeamCode,
				turnovers: [ ...liveEvent.turnovers ],
			}
			: null,
	}
}

function extractLiveDraftEvent(flow: FlowData | null): FlowDraftEvent | null {
	if (flow?.draftEvent) {
		return {
			cpTeamCode: flow.draftEvent.cpTeamCode,
			turnovers: [ ...flow.draftEvent.turnovers ],
		}
	}

	const lastEvent = flow?.events?.[flow.events.length - 1]
	if (!lastEvent || lastEvent.result !== 'I') {
		return null
	}

	return {
		cpTeamCode: lastEvent.cpTeamCode,
		turnovers: [ ...lastEvent.turnovers ],
	}
}

function stripLiveEvent(events?: FlowEvent[] | null): FlowEvent[] {
	if (!events?.length) {
		return [ ]
	}

	const lastEvent = events[events.length - 1]
	if (lastEvent.result !== 'I') {
		return events
	}

	return events.slice(0, -1)
}

function buildFreshCurrentEvent(flowData: EditableFlowData, numPeriods: number): FlowDraftEvent | null {
	if (!flowData.setup || flowData.currentQuarter > numPeriods) {
		return null
	}

	const lastEvent = flowData.events[flowData.events.length - 1]
	const cpTeamCode = lastEvent
		? (
			lastEvent.result === 'X' && didJustEndQuarter(flowData)
				? oppositeSide(lastEvent.cpTeamCode)
				: nextCentrePassSide(lastEvent)
		)
		: flowData.setup.firstCpTeam

	return {
		cpTeamCode,
		turnovers: [ ],
	}
}

function endIncompleteEvent(currentEvent: FlowDraftEvent): FlowEvent {
	return {
		cpTeamCode: currentEvent.cpTeamCode,
		turnovers: [ ...currentEvent.turnovers ],
		result: 'X',
	}
}

function oppositeSide(side: FlowTeamSide): FlowTeamSide {
	return side === 'home' ? 'away' : 'home'
}

function oppositeDirection(direction: FlowAttackDirection): FlowAttackDirection {
	return direction === 'left' ? 'right' : 'left'
}

function getPossessionSide(currentEvent: FlowDraftEvent): FlowTeamSide {
	return currentEvent.turnovers.length % 2 === 0
		? currentEvent.cpTeamCode
		: oppositeSide(currentEvent.cpTeamCode)
}

function getDefensiveZoneForSide(side: FlowTeamSide): FlowEventZone {
	return side === 'home' ? 'D' : 'A'
}

function getAttackingZoneForSide(side: FlowTeamSide): FlowEventZone {
	return side === 'home' ? 'A' : 'D'
}

function getBlockedTurnoverZones(currentEvent: FlowDraftEvent): Set<FlowEventZone> {
	const lastTurnoverZone = currentEvent.turnovers[currentEvent.turnovers.length - 1]
	const possessionSide = getPossessionSide(currentEvent)

	if (!lastTurnoverZone || lastTurnoverZone === 'M') {
		return new Set<FlowEventZone>([ getDefensiveZoneForSide(possessionSide) ])
	}

	if (lastTurnoverZone === getAttackingZoneForSide(possessionSide)) {
		const zones: FlowEventZone[] = [ 'A', 'M', 'D' ]
		return new Set<FlowEventZone>(zones.filter(zone => zone !== lastTurnoverZone))
	}

	return new Set<FlowEventZone>()
}

function getEventScorerSide(event: FlowEvent): FlowTeamSide | null {
	if (event.result === 'X' || event.result === 'I') {
		return null
	}
	return event.turnovers.length % 2 === 0
		? event.cpTeamCode
		: oppositeSide(event.cpTeamCode)
}

function nextCentrePassSide(event: FlowEvent): FlowTeamSide {
	return event.result === 'X' || event.result === 'I'
		? event.cpTeamCode
		: oppositeSide(event.cpTeamCode)
}

function didJustEndQuarter(flowData: EditableFlowData) {
	return flowData.quarterBreaks.length > 0
		&& flowData.quarterBreaks[flowData.quarterBreaks.length - 1] === flowData.events.length
		&& flowData.currentQuarter > 1
}

function hasCurrentQuarterRecordedActivity(
	flowData: EditableFlowData,
	currentEvent: FlowDraftEvent | null,
) {
	const currentQuarterStartIndex = flowData.quarterBreaks[flowData.currentQuarter - 2] ?? 0
	return flowData.events.slice(currentQuarterStartIndex).some(event => (
		event.turnovers.length > 0 || event.result === 'G' || event.result === 'GG'
	)) || !!currentEvent?.turnovers.length
}

function shouldAskAboutCentrePassAfterGoal(props: {
	flowData: EditableFlowData
	currentEvent: FlowDraftEvent | null
}) {
	const { flowData, currentEvent } = props
	if (!currentEvent || currentEvent.turnovers.length > 0) {
		return false
	}

	const lastEvent = flowData.events[flowData.events.length - 1]
	return lastEvent?.result === 'G' || lastEvent?.result === 'GG'
}

function labelSide(side: FlowTeamSide, homeName?: string | null, awayName?: string | null) {
	return side === 'home' ? (homeName || 'Home') : (awayName || 'Away')
}

function getCourtZones(
	homeAttackDirection: FlowAttackDirection,
	quarter: number,
) {
	const rawZones: FlowEventZone[] = homeAttackDirectionForQuarter(homeAttackDirection, quarter) === 'left'
		? [ 'A', 'M', 'D' ]
		: [ 'D', 'M', 'A' ]

	return rawZones
}

function homeAttackDirectionForQuarter(
	homeAttackDirection: FlowAttackDirection,
	quarter: number,
): FlowAttackDirection {
	if (quarter % 2 === 1) {
		return homeAttackDirection
	}
	return homeAttackDirection === 'left' ? 'right' : 'left'
}

function baseHomeAttackDirectionForQuarter(
	currentQuarterDirection: FlowAttackDirection,
	quarter: number,
): FlowAttackDirection {
	return quarter % 2 === 1
		? currentQuarterDirection
		: oppositeDirection(currentQuarterDirection)
}

function teamAttackDirection(
	homeAttackDirection: FlowAttackDirection,
	side: FlowTeamSide,
): FlowAttackDirection {
	return side === 'home'
		? homeAttackDirection
		: oppositeDirection(homeAttackDirection)
}

function getLastActionType(
	flowData: EditableFlowData,
	currentEvent: FlowDraftEvent | null,
): 'goal' | 'quarterBreak' | 'turnover' | 'none' {
	if (currentEvent?.turnovers.length) {
		return 'turnover'
	}

	const endedQuarter = flowData.quarterBreaks.length > 0
		&& flowData.quarterBreaks[flowData.quarterBreaks.length - 1] === flowData.events.length
		&& flowData.currentQuarter > 1
	if (endedQuarter) {
		return 'quarterBreak'
	}

	const lastEvent = flowData.events[flowData.events.length - 1]
	if (!lastEvent) {
		return 'none'
	}

	return lastEvent.result === 'G' || lastEvent.result === 'GG'
		? 'goal'
		: 'none'
}

function getLastCourtMarker(
	flowData: EditableFlowData,
	currentEvent: FlowDraftEvent | null,
): CourtMarker | null {
	if (currentEvent?.turnovers.length) {
		return {
			type: 'turnover',
			side: getPossessionSide(currentEvent),
			zone: currentEvent.turnovers[currentEvent.turnovers.length - 1],
		}
	}

	const canReopenQuarter = flowData.quarterBreaks.length > 0
		&& flowData.quarterBreaks[flowData.quarterBreaks.length - 1] === flowData.events.length
		&& flowData.currentQuarter > 1
	if (canReopenQuarter) {
		return currentEvent
			? {
				type: 'cp',
				side: currentEvent.cpTeamCode,
			}
			: null
	}

	const lastEvent = flowData.events[flowData.events.length - 1]
	if (!lastEvent) {
		return currentEvent
			? {
				type: 'cp',
				side: currentEvent.cpTeamCode,
			}
			: null
	}

	if (lastEvent.result === 'G' || lastEvent.result === 'GG') {
		const scorer = getEventScorerSide(lastEvent)
		const cpSide = currentEvent?.cpTeamCode || nextCentrePassSide(lastEvent)
		return scorer
			? {
				type: 'goal',
				side: scorer,
				cpSide,
			}
			: null
	}

	if (lastEvent.turnovers.length) {
		return null
	}

	return null
}

function getQuarterForEvent(flowData: EditableFlowData, index: number) {
	let quarter = 1
	while (quarter <= flowData.quarterBreaks.length && index >= flowData.quarterBreaks[quarter - 1]) {
		quarter += 1
	}
	return quarter
}

function describeCourtThird(
	zone: FlowEventZone,
	homeAttackDirection: FlowAttackDirection,
	quarter: number,
	homeName: string,
	awayName: string,
) {
	const labels = getCourtZones(homeAttackDirection, quarter).map(rawZone => {
		if (rawZone === 'M') {
			return 'MIDCOURT'
		}
		const direction = homeAttackDirectionForQuarter(homeAttackDirection, quarter)
		const leftAttackLabel = direction === 'left' ? `${homeName} ATTACK` : `${awayName} ATTACK`
		const rightAttackLabel = direction === 'left' ? `${awayName} ATTACK` : `${homeName} ATTACK`
		return rawZone === getCourtZones(homeAttackDirection, quarter)[0]
			? leftAttackLabel
			: rightAttackLabel
	})
	const zones = getCourtZones(homeAttackDirection, quarter)
	const index = zones.indexOf(zone)
	return labels[index] || zone
}

function describeLastEvent(props: {
	flowData: EditableFlowData
	currentEvent: FlowDraftEvent | null
	homeName: string
	awayName: string
	numPeriods: number
}) {
	const { flowData, currentEvent, homeName, awayName, numPeriods } = props

	if (currentEvent?.turnovers.length) {
		const lastZone = currentEvent.turnovers[currentEvent.turnovers.length - 1]
		return `Turnover in ${describeCourtThird(
			lastZone,
			flowData.setup!.homeAttackDirection,
			flowData.currentQuarter,
			homeName,
			awayName,
		)}`
	}

	const canReopenQuarter = flowData.quarterBreaks.length > 0
		&& flowData.quarterBreaks[flowData.quarterBreaks.length - 1] === flowData.events.length
		&& flowData.currentQuarter > 1
	if (canReopenQuarter) {
		const completedQuarter = flowData.currentQuarter - 1
		return completedQuarter >= numPeriods ? 'End Game' : `End of ${formatPeriodNumber(completedQuarter, numPeriods)}`
	}

	const lastEventIndex = flowData.events.length - 1
	const lastEvent = flowData.events[lastEventIndex]
	if (!lastEvent) {
		return null
	}

	if (lastEvent.result === 'X') {
		return `Unfinished possession ended in ${formatPeriodNumber(getQuarterForEvent(flowData, lastEventIndex), numPeriods)}`
	}
	if (lastEvent.result === 'I') {
		return `Possession in progress in ${formatPeriodNumber(getQuarterForEvent(flowData, lastEventIndex), numPeriods)}`
	}

	const scorer = getEventScorerSide(lastEvent)
	const scorerName = scorer === 'home' ? homeName : awayName
	return lastEvent.result === 'GG'
		? `Super Goal for ${scorerName}`
		: `Goal for ${scorerName}`
}

function directionalArrow(props: {
	x: number
	y: number
	direction: FlowAttackDirection
	length: number
	headSize: number
}) {
	const { x, y, direction, length, headSize } = props
	const halfLength = length / 2
	if (direction === 'right') {
		const tipX = x + halfLength
		const baseX = tipX - headSize
		return {
			line: {
				x1: x - halfLength,
				y1: y,
				x2: baseX,
				y2: y,
			},
			headPoints: `${baseX},${y - (headSize * 0.6)} ${tipX},${y} ${baseX},${y + (headSize * 0.6)}`,
		}
	}

	const tipX = x - halfLength
	const baseX = tipX + headSize
	return {
		line: {
			x1: x + halfLength,
			y1: y,
			x2: baseX,
			y2: y,
		},
		headPoints: `${baseX},${y - (headSize * 0.6)} ${tipX},${y} ${baseX},${y + (headSize * 0.6)}`,
	}
}

function directionalArrowFromBase(props: {
	x: number
	y: number
	direction: FlowAttackDirection
	length: number
	headSize: number
}) {
	const { x, y, direction, length, headSize } = props
	if (direction === 'right') {
		const tipX = x + length
		const headBaseX = tipX - headSize
		return {
			line: {
				x1: x,
				y1: y,
				x2: headBaseX,
				y2: y,
			},
			headPoints: `${headBaseX},${y - (headSize * 0.6)} ${tipX},${y} ${headBaseX},${y + (headSize * 0.6)}`,
		}
	}

	const tipX = x - length
	const headBaseX = tipX + headSize
	return {
		line: {
			x1: x,
			y1: y,
			x2: headBaseX,
			y2: y,
		},
		headPoints: `${headBaseX},${y - (headSize * 0.6)} ${tipX},${y} ${headBaseX},${y + (headSize * 0.6)}`,
	}
}

function calculateScore(events: FlowEvent[]) {
	return events.reduce((score, event) => {
		const scorer = event.result === 'X' || event.result === 'I'
			? null
			: event.turnovers.length % 2 === 0
				? event.cpTeamCode
				: oppositeSide(event.cpTeamCode)

		if (!scorer) {
			return score
		}

		const points = event.result === 'GG' ? 2 : 1
		return scorer === 'home'
			? { home: score.home + points, away: score.away }
			: { home: score.home, away: score.away + points }
	}, {
		home: 0,
		away: 0,
	})
}

function readableTextColor(hexColor: string) {
	const normalized = hexColor.replace('#', '')
	const value = normalized.length === 3
		? normalized.split('').map(char => `${char}${char}`).join('')
		: normalized

	const red = Number.parseInt(value.slice(0, 2), 16)
	const green = Number.parseInt(value.slice(2, 4), 16)
	const blue = Number.parseInt(value.slice(4, 6), 16)
	const brightness = ((red * 299) + (green * 587) + (blue * 114)) / 1000

	return brightness > 160 ? '#0f172a' : '#f8fafc'
}

function deriveGoalButtonColor(hexColor: string) {
	const brightness = colorBrightness(hexColor)
	if (brightness < 140) {
		return mixHexColors(hexColor, '#ffffff', 0.18)
	}
	if (brightness > 195) {
		return mixHexColors(hexColor, '#0f172a', 0.16)
	}
	return mixHexColors(hexColor, '#ffffff', 0.1)
}

function colorBrightness(hexColor: string) {
	const value = normalizeHexColor(hexColor)
	const red = Number.parseInt(value.slice(0, 2), 16)
	const green = Number.parseInt(value.slice(2, 4), 16)
	const blue = Number.parseInt(value.slice(4, 6), 16)
	return ((red * 299) + (green * 587) + (blue * 114)) / 1000
}

function mixHexColors(baseHexColor: string, mixHexColor: string, amount: number) {
	const base = normalizeHexColor(baseHexColor)
	const mix = normalizeHexColor(mixHexColor)
	const weight = Math.max(0, Math.min(1, amount))
	const red = Math.round((Number.parseInt(base.slice(0, 2), 16) * (1 - weight)) + (Number.parseInt(mix.slice(0, 2), 16) * weight))
	const green = Math.round((Number.parseInt(base.slice(2, 4), 16) * (1 - weight)) + (Number.parseInt(mix.slice(2, 4), 16) * weight))
	const blue = Math.round((Number.parseInt(base.slice(4, 6), 16) * (1 - weight)) + (Number.parseInt(mix.slice(4, 6), 16) * weight))
	return `#${[ red, green, blue ].map(value => value.toString(16).padStart(2, '0')).join('')}`
}

function withAlpha(hexColor: string, alpha: number) {
	const value = normalizeHexColor(hexColor)
	const red = Number.parseInt(value.slice(0, 2), 16)
	const green = Number.parseInt(value.slice(2, 4), 16)
	const blue = Number.parseInt(value.slice(4, 6), 16)

	return `rgb(${red} ${green} ${blue} / ${alpha})`
}

function normalizeHexColor(hexColor: string) {
	const normalized = hexColor.replace('#', '')
	return normalized.length === 3
		? normalized.split('').map(char => `${char}${char}`).join('')
		: normalized.slice(0, 6).padEnd(6, '0')
}

function describeGoalArc(props: {
	x: number
	y: number
	radius: number
	isLeftSide: boolean
}) {
	const { x, y, radius, isLeftSide } = props
	const top = y - radius
	const bottom = y + radius
	return isLeftSide
		? `M ${x} ${top} A ${radius} ${radius} 0 0 1 ${x} ${bottom}`
		: `M ${x} ${top} A ${radius} ${radius} 0 0 0 ${x} ${bottom}`
}

export default RecordFlows
